import { organizations, providerOrganizations, providers } from "./globalSchema.mjs";
import { tenants } from "./authSchema.mjs";
import { phoneCalls } from "./customerSchema.mjs";
import { relations } from "drizzle-orm";
import { boolean, foreignKey, index, pgTable, text, timestamp, unique, uuid } from "drizzle-orm/pg-core";

//#region src/schema/networkSchema.ts
let NetworkVerificationMode = /* @__PURE__ */ function(NetworkVerificationMode) {
	/** A directory listing is enough to send. */
	NetworkVerificationMode["DirectoryTrusted"] = "directory_trusted";
	/** Only an office's own answer on a call counts. */
	NetworkVerificationMode["CallToVerify"] = "call_to_verify";
	return NetworkVerificationMode;
}({});
/**
* A payer or employer network, e.g. PHCS on MultiPlan or Memorial Hermann.
* Shared across tenants, like the rest of the directory: membership answers
* are facts about an office, so one customer's call saves another's.
*/
const networks = pgTable("networks", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	name: text("name").notNull(),
	verificationMode: text("verification_mode", { enum: [NetworkVerificationMode.DirectoryTrusted, NetworkVerificationMode.CallToVerify] }).notNull(),
	strict: boolean("strict").notNull().default(false),
	directoryUrl: text("directory_url"),
	ownerTenantId: text("owner_tenant_id").references(() => tenants.id),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
}, (t) => [unique("networks_name_owner_tenant_unique").on(t.name, t.ownerTenantId).nullsNotDistinct()]);
let NetworkMembershipStatus = /* @__PURE__ */ function(NetworkMembershipStatus) {
	/** In the network's directory, not yet verified with the office. */
	NetworkMembershipStatus["Listed"] = "listed";
	NetworkMembershipStatus["Confirmed"] = "confirmed";
	/** The office said no. Kept so the agent never calls to ask again. */
	NetworkMembershipStatus["Refuted"] = "refuted";
	return NetworkMembershipStatus;
}({});
let NetworkMembershipSource = /* @__PURE__ */ function(NetworkMembershipSource) {
	NetworkMembershipSource["DirectoryImport"] = "directory_import";
	NetworkMembershipSource["PhoneCall"] = "phone_call";
	NetworkMembershipSource["Staff"] = "staff";
	return NetworkMembershipSource;
}({});
const networkMemberships = pgTable("network_memberships", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	networkId: uuid("network_id").notNull().references(() => networks.id),
	organizationId: uuid("organization_id").notNull().references(() => organizations.id),
	providerId: uuid("provider_id"),
	status: text("status", { enum: [
		NetworkMembershipStatus.Listed,
		NetworkMembershipStatus.Confirmed,
		NetworkMembershipStatus.Refuted
	] }).notNull(),
	source: text("source", { enum: [
		NetworkMembershipSource.DirectoryImport,
		NetworkMembershipSource.PhoneCall,
		NetworkMembershipSource.Staff
	] }).notNull(),
	phoneCallId: uuid("phone_call_id").references(() => phoneCalls.id, { onDelete: "set null" }),
	listedAt: timestamp("listed_at"),
	verifiedAt: timestamp("verified_at"),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
}, (t) => [
	unique("network_memberships_network_org_provider_unique").on(t.networkId, t.organizationId, t.providerId).nullsNotDistinct(),
	foreignKey({
		name: "network_memberships_provider_organization_fk",
		columns: [t.providerId, t.organizationId],
		foreignColumns: [providerOrganizations.providerId, providerOrganizations.organizationId]
	}),
	index("idx_network_memberships_organization_id").on(t.organizationId)
]);
const networksRelations = relations(networks, ({ one, many }) => ({
	ownerTenant: one(tenants, {
		fields: [networks.ownerTenantId],
		references: [tenants.id]
	}),
	memberships: many(networkMemberships)
}));
const networkMembershipsRelations = relations(networkMemberships, ({ one }) => ({
	network: one(networks, {
		fields: [networkMemberships.networkId],
		references: [networks.id]
	}),
	organization: one(organizations, {
		fields: [networkMemberships.organizationId],
		references: [organizations.id]
	}),
	provider: one(providers, {
		fields: [networkMemberships.providerId],
		references: [providers.id]
	}),
	phoneCall: one(phoneCalls, {
		fields: [networkMemberships.phoneCallId],
		references: [phoneCalls.id]
	})
}));

//#endregion
export { NetworkMembershipSource, NetworkMembershipStatus, NetworkVerificationMode, networkMemberships, networkMembershipsRelations, networks, networksRelations };